import React, { useEffect, useMemo, useState } from "react"
import { ColumnDef, getExpandedRowModel } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import Chip from "Components/atoms/chip"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import withRouter from "Components/Common/withRouter"
import DataTableTemplate from "Components/templates/data-table-template"
import ActionButton from "Components/atoms/buttons/action-button"
import TreeNameCell from "Components/atoms/tree-name-cell"
import { compareText } from "utils/department-options"
import Department, { DepartmentBillable, DEPARTMENT_BILLABLE_VARIANT } from "models/department"
import {
    AdminDepartmentsSliceState,
    setDepartments,
    setIsGlobalAdmin,
} from "slices/admin/departments/reducer"
import { getDepartments } from "slices/admin/departments/thunk"
import { navigateToCreateDepartment, navigateToDepartmentDetails } from "utils/navigator"
import { PlusIcon, HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"


const selectDepartments = createSelector(
    (state: any) => state.AdminDepartments,
    ({ departments, isGlobalAdmin }: AdminDepartmentsSliceState) => ({
        departments,
        isGlobalAdmin
    })
)

type DepartmentNode = Department & { subRows?: DepartmentNode[] }

const DepartmentsPage = (props: any) => {
    document.title = "Departments | IbexIQ";
    const { handleOpenManual } = useManual("admin/departments");
    const dispatch: any = useDispatch()
    const { departments, isGlobalAdmin } = useSelector(selectDepartments)

    const [isLoading, setIsLoading] = useState<boolean>(true)

    useEffect(() => {
        const init = async () => {
            if (departments.length === 0) {
                const res = await getDepartments()
                if (res) {
                    dispatch(setDepartments(res.departments))
                    dispatch(setIsGlobalAdmin(res.isGlobalAdmin))
                }
            }
            setIsLoading(false)
        }
        init()
    }, [])

    const handleAddDepartment = () => navigateToCreateDepartment(props.router.navigate)
    const handleOpenDepartment = (department: Department) => navigateToDepartmentDetails(props.router.navigate, department.id)

    // Hierarchy: each department under its parent (company > division > department), A-Z, indented.
    // Delete lives on the department's page.
    const columns = React.useMemo<ColumnDef<DepartmentNode>[]>(() => [
        {
            header: "Department",
            accessorKey: "description",
            size: 320,
            cell: ({ row }) => <TreeNameCell row={row}>
                <span className="d-inline-flex align-items-center gap-2">
                    <span className="department-color-dot" style={{ backgroundColor: row.original.color ?? 'transparent' }} />
                    <a className="table-link" role="button" onClick={(e) => { e.stopPropagation(); handleOpenDepartment(row.original) }}>
                        {row.original.description}
                    </a>
                </span>
            </TreeNameCell>
        },
        {
            header: "Code",
            accessorKey: "code",
            size: 100,
        },
        {
            header: "Level",
            id: "level",
            size: 60,
            accessorFn: (d) => d.departmentLevel?.description,
            cell: ({ row }) => row.original.departmentLevel?.description ?? "—"
        },
        {
            header: "Billable",
            id: "isBillable",
            size: 60,
            cell: ({ row }) => {
                const billable = row.original.isBillable ? DepartmentBillable.YES : DepartmentBillable.NO
                return <Chip variant={DEPARTMENT_BILLABLE_VARIANT[billable]}>{billable}</Chip>
            },
            accessorFn: (row) => row.isBillable ? DepartmentBillable.YES : DepartmentBillable.NO,
        },
        {
            header: "Head",
            id: "head",
            size: 120,
            accessorFn: (d) => d.departmentHead
                ? (d.departmentHead.commonName ?? `${d.departmentHead.firstName} ${d.departmentHead.lastName}`)
                : undefined,
            cell: ({ row }) => {
                const head = row.original.departmentHead
                return head ? (head.commonName ?? `${head.firstName} ${head.lastName}`) : "—"
            }
        },
        {
            header: "Your Access",
            id: "yourPermission",
            size: 80,
            accessorFn: (d) => d.yourPermission ?? undefined,
            cell: ({ row }) => row.original.yourPermission ?? "—"
        }
    ], [])

    const tree = useMemo<DepartmentNode[]>(() => {
        const ids = new Set(departments.map((d: Department) => d.id))
        const build = (parentId: string | null): DepartmentNode[] => departments
            .filter((d: Department) => (parentId === null ? !d.parentId || !ids.has(d.parentId) : d.parentId === parentId))
            .sort((a: Department, b: Department) => compareText(a.description, b.description))
            .map((d: Department) => {
                const children = build(d.id)
                return { ...d, subRows: children.length ? children : undefined }
            })
        return build(null)
    }, [departments])

    const table = useDataTable({
        columns,
        data: tree,
        getSubRows: (row) => row.subRows,
        getExpandedRowModel: getExpandedRowModel(),
        initialState: { expanded: true },
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })

    return <DataTableTemplate
        title="Admin"
        breadcrumbItem="Departments"
        table={table}
        columns={columns}
        isLoading={isLoading}
        actions={<>
            {isGlobalAdmin && <ActionButton onClick={handleAddDepartment}>New Department <PlusIcon /></ActionButton>}
            <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
        </>}
    />
}

export default withRouter(DepartmentsPage)
